import { useState, FormEvent, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/services/api";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Invalid reset link. Please request a new password reset.");
    }
  }, [token]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    if (!token) {
      setError("Invalid reset token.");
      return;
    }

    setLoading(true);

    try {
      await api.post("/auth/reset-password", {
        token,
        password,
      });

      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to reset password. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-deep-black flex items-center justify-center px-4 font-inter">
        <div className="w-full max-w-md">
          <div className="bg-dark-panel border border-dark-border rounded-2xl p-8 text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-cyber-green/10 border border-cyber-green/30 mx-auto mb-5">
              <i className="ri-check-line text-2xl text-cyber-green" />
            </div>
            <h2 className="text-white font-bold text-xl mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Password Reset Successful
            </h2>
            <p className="text-silver text-sm mb-6">
              Your password has been updated. You can now sign in with your new password.
            </p>
            <Link 
              to="/login" 
              className="inline-block bg-electric-blue text-deep-black font-bold px-6 py-3 rounded-xl hover:shadow-glow-blue transition-all"
            >
              Go to Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-black flex items-center justify-center px-4 font-inter">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 mb-10">
          <img src="https://static.readdy.ai/image/8f57b6c24a127be3ec5c87bfaa3e1cdb/10eec45dd18ea3b56875e869c153a69b.png" alt="Logo" className="h-7 w-auto" />
          <span className="font-bold text-white text-base" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>SCHEMACKOZO</span>
        </Link>

        <div className="bg-dark-panel border border-dark-border rounded-2xl p-8">
          <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-electric-blue/10 border border-electric-blue/30 mb-6">
            <i className="ri-lock-password-line text-xl text-electric-blue" />
          </div>
          <h1 className="text-2xl font-black text-white mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Set new password</h1>
          <p className="text-silver text-sm mb-7">Enter your new password below.</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-silver text-xs font-medium mb-2 uppercase tracking-wider">New Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Minimum 6 characters"
                className="w-full bg-deep-black border border-dark-border rounded-xl px-4 py-3 text-white text-sm placeholder-silver-dim focus:outline-none focus:border-electric-blue/50 transition-colors"
              />
            </div>
            <div>
              <label className="block text-silver text-xs font-medium mb-2 uppercase tracking-wider">Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Confirm your new password"
                className="w-full bg-deep-black border border-dark-border rounded-xl px-4 py-3 text-white text-sm placeholder-silver-dim focus:outline-none focus:border-electric-blue/50 transition-colors"
              />
            </div>
            {error && <div className="text-cyber-orange text-sm">{error}</div>}
            <button
              type="submit"
              disabled={loading || !token}
              className="w-full bg-electric-blue text-deep-black font-bold py-3.5 rounded-xl hover:shadow-glow-blue transition-all duration-300 disabled:opacity-50 text-sm whitespace-nowrap cursor-pointer"
            >
              {loading ? <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin" />Resetting...</span> : "Reset Password"}
            </button>
          </form>

          <p className="text-center text-silver-dim text-sm mt-5">
            <Link to="/login" className="text-electric-blue hover:underline cursor-pointer flex items-center justify-center gap-1">
              <i className="ri-arrow-left-line text-sm" /> Back to sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
